import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Button } from './Button'
import { Modal } from './Modal'

/**
 * Ask before destroying a record.
 *
 * ## Why every record, and not a judgment per screen
 *
 * The obvious objection to always confirming is that it trains people to click
 * through it, and that is true of a dialog on every *action*. It is not true
 * here, because deleting a record is already rare and already deliberate: it
 * sits at the end of the record's own panel, reached by scrolling past every
 * setting, or behind a row's trailing menu. Somebody who arrives at it arrives
 * on purpose. The dialog is not fighting muscle memory — it is the last chance
 * before a thing stops existing.
 *
 * The alternative — deciding per screen — is what produced three different
 * delete treatments in the first place, and the same argument would have to be
 * had again for every record type added.
 *
 * **A row is not a record.** Removing a contact, a yahrzeit or a relative is
 * editing a list that already writes as you go, and re-adding it is the undo.
 * Those do not confirm; a rule, a message, a task, a household, a person and a
 * config value do.
 *
 * ## Why a promise
 *
 * The call site reads as a sentence — ask, then act — instead of splitting one
 * decision across a state flag, an effect and a second handler. The dialog is
 * rendered once wherever the hook is used.
 *
 * ```
 * const { confirm, dialog } = useConfirm()
 * …
 * onSelect: async () => {
 *   if (await confirm({ title: 'Delete this rule?', body: '…' })) remove()
 * }
 * …
 * return <>{dialog}…</>
 * ```
 */
type Ask = {
  title: string
  /** What it costs, stated plainly. Not "are you sure" — say what happens. */
  body: ReactNode
  /** The button's own words: "Delete rule", never "OK". */
  verb?: string
  /**
   * Whether what is being asked DESTROYS something.
   *
   * Destruction is the ordinary case and stays the default. The other one is an
   * act that is merely hard to take back — money moved between two families,
   * where both balances change and neither is lost. `--danger` is destruction
   * and failure only, so an act that destroys nothing takes the ordinary
   * commit, and the dialog still says what it costs.
   */
  destructive?: boolean
}

export function useConfirm() {
  const [ask, setAsk] = useState<Ask | null>(null)
  const answer = useRef<((ok: boolean) => void) | null>(null)

  /* One question is open at a time. A second ask answers the first as
     declined, and so does the dialog leaving the screen, so no caller is left
     awaiting an answer that can no longer come. */
  const confirm = useCallback(
    (next: Ask) =>
      new Promise<boolean>((resolve) => {
        answer.current?.(false)
        answer.current = resolve
        setAsk(next)
      }),
    [],
  )
  useEffect(
    () => () => {
      answer.current?.(false)
      answer.current = null
    },
    [],
  )

  const settle = useCallback((ok: boolean) => {
    // Resolve BEFORE clearing, and clear the ref either way: an unanswered
    // promise from a dismissed dialog would leave the caller awaiting for ever.
    answer.current?.(ok)
    answer.current = null
    setAsk(null)
  }, [])

  /* Destruction is the ordinary case, so an ask that says nothing asks about
     one. */
  const destructive = ask?.destructive ?? true
  const dialog = ask ? (
    <Modal
      title={ask.title}
      onClose={() => settle(false)}
      footer={
        /* Both answers trail, together. A dialog's answer sits where an answer
           sits, and a decision deserves both of them side by side: here the two
           ARE the question. Its verb may be destructive, which is why it draws
           its own pair rather than the Foot, whose primary never is. */
        <span className="uifoot__end">
          <Button variant="secondary" onClick={() => settle(false)}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'danger' : 'default'}
            onClick={() => settle(true)}
          >
            {ask.verb ?? 'Delete'}
          </Button>
        </span>
      }
    >
      {ask.body}
    </Modal>
  ) : null

  return { confirm, dialog }
}
